import React, { useEffect, useRef } from 'react';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';

import { MatterContentSkeleton } from '@/components/ui/skeletons/MatterSkeleton';

import useMatterSelection from '../../hooks/useMatterSelection';
import { AgentChatWorkspace } from '../agent-chat-workspace/AgentChatWorkspace';
import { ArtifactRoutes } from '../artifacts/ArtifactRoutes';
import { ClientCommunicationsTab } from '../client-communications';
import CMSSyncTab from '../cms-sync/CMSSyncTab';
import { CorrespondenceRoutes } from '../correspondence/CorrespondenceRoutes';
import { CoverageRoutes } from '../coverage/CoverageRoutes';
import { DeliverableRoutes } from '../deliverables-page/DeliverableRoutes';
import { DemandReviewRoutes } from '../demand-review/DemandReviewRoutes';
import { DocumentExplorer } from '../documents-v2/DocumentExplorer';
import { CaseExpensesPage } from '../ledger/case-expenses/CaseExpensesPage';
import { LedgerOverview } from '../ledger/LedgerOverview';
import Matter from '../matter/Matter';
import { MatterWiki } from '../matter-wiki/MatterWiki';
import { MatterServicesTab } from '../matters/MatterServicesTab';
import { RiskSnapshotPanel } from '../mobrain/RiskSnapshotPanel';
import { PoliceRoutes } from '../police/PoliceRoutes';
import { TreatmentRoutes } from '../treatment/TreatmentRoutes';
import { DemandPanel } from './DemandPanel';
import MatterFaxTab from './MatterFaxTab';
import MatterMailTab from './MatterMailTab';
import WelcomeScreen from './WelcomeScreen';

const MatterPanel: React.FC = () => {
  const { selectedMatterData, selectedMatterLoading } = useMatterSelection();
  const mainContentRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();

  // Scroll to top when selectedMatterId changes
  useEffect(() => {
    if (mainContentRef.current) {
      mainContentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [selectedMatterData?.id, location.pathname]);

  // If no data and not loading, show welcome screen
  if (!selectedMatterData && !selectedMatterLoading) {
    return (
      <WelcomeScreen
        message="No matters found... yet!"
        description="Select or create a matter to get started with Finch. If you've just completed a call, refresh this page to see your generated matter."
      />
    );
  }

  if (selectedMatterLoading) {
    return <MatterContentSkeleton />;
  }

  const matterId = selectedMatterData?.id;
  const firmId = selectedMatterData?.firm?.id;

  if (!matterId || !firmId) {
    navigate('/matters');
    return null;
  }

  return (
    <div ref={mainContentRef} className="h-full w-full">
      <Routes>
        <Route path="/" element={<Matter matter={selectedMatterData} />} />
        <Route
          path="/services/*"
          element={<MatterServicesTab matterId={matterId} />}
        />
        <Route path="/documents/*" element={<DocumentExplorer />} />
        <Route path="/treatment/*" element={<TreatmentRoutes />} />
        <Route path="/coverage/*" element={<CoverageRoutes />} />
        <Route
          path="/risk/*"
          element={<RiskSnapshotPanel matterId={matterId} />}
        />
        <Route path="/ledger/*" element={<LedgerOverview />} />
        <Route path="/case-expenses/*" element={<CaseExpensesPage />} />
        <Route path="/police/*" element={<PoliceRoutes />} />
        <Route
          path="/client-comms/*"
          element={<ClientCommunicationsTab matter={selectedMatterData} />}
        />
        <Route path="/correspondence/*" element={<CorrespondenceRoutes />} />
        <Route path="/mail/*" element={<MatterMailTab />} />
        <Route path="/fax/*" element={<MatterFaxTab />} />
        <Route path="/artifacts/*" element={<ArtifactRoutes />} />
        <Route path="/deliverables/*" element={<DeliverableRoutes />} />
        <Route
          path="/workspaces/*"
          element={<AgentChatWorkspace matterId={matterId} firmId={firmId} />}
        />
        <Route path="/demand/*" element={<DemandPanel />} />
        <Route path="/document-review/*" element={<DemandReviewRoutes />} />
        <Route path="/cms-sync/*" element={<CMSSyncTab />} />
        <Route path="/wiki/*" element={<MatterWiki matterId={matterId} />} />
      </Routes>
    </div>
  );
};

export default MatterPanel;
